import type {
  DataObjectFieldsSchemaRaw,
  DataObjectFilterConfigRaw
} from "@certa/types/src/dataObjects.types";
import type { ObjectReportColumnFieldTags as ObjectReportColumnFieldTagsType } from "../constants";
import { ObjectReportColumnFieldTags } from "../constants";

export type FieldCapabilities = {
  isIndexed: boolean;
  isFilterable: boolean;
  isSearchable: boolean;
  filterConfig?: {
    type: "select" | "single_select";
    options?: { label: string; value: string }[];
    optionUrl?: string;
  };
};

/**
 * Check if a field is indexed (supports sorting)
 */
const isFieldIndexed = (
  fieldTag: string,
  schema: DataObjectFieldsSchemaRaw
): boolean => {
  return schema?.properties?.[fieldTag]?.indexed === true;
};

/**
 * Check if a field is filterable (has filter config or is an indexed date field)
 */
const isFieldFilterable = (
  fieldTag: string,
  filterConfig?: DataObjectFilterConfigRaw,
  schema?: DataObjectFieldsSchemaRaw
): boolean => {
  // Check if field has explicit filter configuration
  const hasFilterConfig = !!(
    filterConfig?.single_select?.[fieldTag] ||
    filterConfig?.multi_select?.[fieldTag]
  );

  // Check if field is an indexed date/datetime field
  const fieldType = schema?.properties?.[fieldTag]?.type;
  const isIndexed = schema?.properties?.[fieldTag]?.indexed === true;
  const isIndexedDateField =
    (fieldType === "date" || fieldType === "datetime") && isIndexed;

  return hasFilterConfig || isIndexedDateField;
};

/**
 * Check if a field is searchable (indexed text field without filter config)
 */
const isFieldSearchable = (
  fieldTag: string,
  schema: DataObjectFieldsSchemaRaw,
  filterConfig?: DataObjectFilterConfigRaw
): boolean => {
  // Field must be indexed
  if (!isFieldIndexed(fieldTag, schema)) return false;

  // Field should not have filter config (if it has filter config, use filtering instead)
  if (isFieldFilterable(fieldTag, filterConfig, schema)) return false;

  // Field should be a text type
  const fieldType = schema?.properties?.[fieldTag]?.type;
  return fieldType === "string" || fieldType === "text";
};

const getFieldFilterConfig = (
  fieldTag: string,
  filterConfig?: DataObjectFilterConfigRaw
) => {
  if (!filterConfig) return undefined;

  if (filterConfig.single_select?.[fieldTag]) {
    return {
      type: "single_select" as const,
      options: filterConfig.single_select[fieldTag].options,
      optionUrl: filterConfig.single_select[fieldTag].option_url
    };
  }

  if (filterConfig.multi_select?.[fieldTag]) {
    return {
      type: "select" as const,
      options: filterConfig.multi_select[fieldTag].options,
      optionUrl: filterConfig.multi_select[fieldTag].option_url
    };
  }

  return undefined;
};

/**
 * Get comprehensive field capabilities
 */
export const getFieldCapabilities = (
  fieldTag: string,
  schema: DataObjectFieldsSchemaRaw,
  filterConfig?: DataObjectFilterConfigRaw
): FieldCapabilities => {
  const isIndexed = isFieldIndexed(fieldTag, schema);
  const isFilterable = isFieldFilterable(fieldTag, filterConfig, schema);
  const isSearchable = isFieldSearchable(fieldTag, schema, filterConfig);
  const fieldFilterConfig = getFieldFilterConfig(fieldTag, filterConfig);

  return {
    isIndexed,
    isFilterable,
    isSearchable,
    filterConfig: fieldFilterConfig
  };
};

/**
 * Get smart column capabilities
 */
export const getSmartColumnCapabilities = (
  fieldTag: ObjectReportColumnFieldTagsType,
  statusOptions?: { label: string; value: string }[],
  badgesOptions?: { label: string; value: string }[]
): FieldCapabilities => {
  switch (fieldTag) {
    case ObjectReportColumnFieldTags.OBJECT_NAME:
      return {
        isIndexed: true,
        isFilterable: false,
        isSearchable: true
      };
    case ObjectReportColumnFieldTags.OBJECT_STATUS:
      return {
        isIndexed: true,
        isFilterable: true,
        isSearchable: false,
        filterConfig: {
          type: "select",
          options: statusOptions || []
        }
      };
    case ObjectReportColumnFieldTags.OBJECT_STATUS_NAME:
      return {
        isIndexed: true,
        isFilterable: true,
        isSearchable: false,
        filterConfig: {
          type: "select",
          options: statusOptions || []
        }
      };
    case ObjectReportColumnFieldTags.OBJECT_STATUS_COLOR_CODE:
      return {
        isIndexed: false,
        isFilterable: false,
        isSearchable: false
      };
    case ObjectReportColumnFieldTags.OBJECT_CREATED_AT:
    case ObjectReportColumnFieldTags.OBJECT_UPDATED_AT:
      return {
        isIndexed: true,
        isFilterable: true,
        isSearchable: false
      };
    case ObjectReportColumnFieldTags.BADGE_TAGS:
      return {
        isIndexed: false,
        isFilterable: true,
        isSearchable: false,
        filterConfig: {
          type: "select",
          options: badgesOptions || []
        }
      };
    default:
      return {
        isIndexed: false,
        isFilterable: false,
        isSearchable: false
      };
  }
};

/**
 * Get only indexed fields for advanced filter cascader
 */
export const getIndexedFields = (
  schema: DataObjectFieldsSchemaRaw
): string[] => {
  if (!schema?.properties) return [];

  return Object.keys(schema.properties).filter(fieldTag =>
    isFieldIndexed(fieldTag, schema)
  );
};
